'use client';

import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

const annotations = [
  {
    id: 'breadcrumb',
    label: 'Project Navigation',
    desc: 'Quickly jump between projects, steps, and the editor from the breadcrumb bar.',
    top: '2%',
    left: '15%'
  },
  {
    id: 'toolbar',
    label: 'Editing Toolbar',
    desc: 'Select, shape, text, upload, zoom, undo/redo — all your editing tools in one bar.',
    top: '7%',
    left: '15%'
  },
  {
    id: 'upload-media',
    label: 'Upload Custom Media',
    desc: 'Import your own images, videos, and assets directly into the editor canvas.',
    top: '7%',
    left: '22%'
  },
  {
    id: 'skip-silences',
    label: 'Skip Silences',
    desc: 'Automatically detect and skip silent segments to keep your tutorials tight.',
    top: '7%',
    left: '44%'
  },
  {
    id: 'save',
    label: 'Save & History',
    desc: 'Save your progress and access full version history to restore any previous state.',
    top: '7%',
    left: '90%'
  },
  {
    id: 'export',
    label: 'Export Video',
    desc: 'Export your polished tutorial video in HD with one click — ready to publish.',
    top: '7%',
    left: '97%'
  },
  {
    id: 'sidebar',
    label: 'Left Sidebar',
    desc: 'Switch between script view, assets, pages, analytics, settings, and comments.',
    top: '18%',
    left: '2%'
  },
  {
    id: 'canvas',
    label: 'Video Canvas',
    desc: 'Preview and position text overlays, captions, and elements with drag-and-drop.',
    top: '35%',
    left: '38%'
  },
  {
    id: 'caption-overlay',
    label: 'Smart Captions',
    desc: 'Auto-generated word-by-word captions positioned directly on the video canvas.',
    top: '62%',
    left: '42%'
  },
  {
    id: 'layout',
    label: 'Layout & Alignment',
    desc: 'Align elements with precision using horizontal, vertical, and distribute controls.',
    top: '32%',
    left: '84%'
  },
  {
    id: 'properties',
    label: 'Position & Dimensions',
    desc: 'Fine-tune X, Y position, width, height, and rotation for pixel-perfect placement.',
    top: '40%',
    left: '88%'
  },
  {
    id: 'typography',
    label: 'Fill & Opacity',
    desc: 'Control fill color, opacity, and corner radius for elements on your video canvas.',
    top: '52%',
    left: '88%'
  },
  {
    id: 'playback',
    label: 'Playback Controls',
    desc: 'Play, pause, skip, and scrub through your video with frame-accurate controls.',
    top: '70%',
    left: '52%'
  },
  {
    id: 'timeline',
    label: 'Multi-track Timeline',
    desc: 'Layer video clips, audio narration, and captions across separate tracks.',
    top: '86%',
    left: '51%'
  },
  {
    id: 'zoom',
    label: 'Zoom & Loop',
    desc: 'Zoom in/out of the timeline, toggle loop, and adjust volume for precise editing.',
    top: '74%',
    left: '92%'
  },
  {
    id: 'audio-wave',
    label: 'Audio Waveforms',
    desc: 'Visualize audio tracks with waveforms for precise editing and syncing.',
    top: '93%',
    left: '30%'
  }
];

export default function EditorShowcase() {
  const [activeAnnotation, setActiveAnnotation] = useState<string | null>(null);

  return (
    <section
      id="product"
      className="py-14 sm:py-20 md:py-24 bg-background"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <span className="inline-flex items-center rounded-full border border-videomule-green/20 px-3 py-1 text-xs font-medium text-videomule-green mb-4 bg-videomule-light-green dark:bg-videomule-green/10">
            Editor
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground">
            Inside the VideoMule Editor
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-sm sm:text-base md:text-lg">
            A professional timeline editor built for tutorial creators — trim,
            narrate, caption, and export with ease.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-2xl border border-border bg-card shadow-2xl group"
        >
          <img
            src="/assets/homepage/editor-screenshot.png"
            alt="VideoMule editor interface showing timeline, video preview, and editing tools"
            className="w-full h-auto rounded-2xl transition-all duration-500"
            style={{
              filter: activeAnnotation ? 'brightness(0.7)' : undefined
            }}
            loading="lazy"
          />

          {/* Annotation hotspots — desktop only */}
          <div className="hidden lg:block">
            {annotations.map((a) => {
              const leftNum = parseFloat(a.left);
              const topNum = parseFloat(a.top);
              const tooltipLeft =
                leftNum > 75 ? 'auto' : leftNum < 25 ? '0' : '50%';
              const tooltipRight = leftNum > 75 ? '0' : 'auto';
              const tooltipTranslateX =
                leftNum > 75 || leftNum < 25 ? '0' : '-50%';
              const tooltipTop = topNum > 80 ? 'auto' : '28px';
              const tooltipBottom = topNum > 80 ? '28px' : 'auto';

              return (
                <div
                  key={a.id}
                  className={`absolute transition-opacity duration-200 ${
                    activeAnnotation === null
                      ? 'z-10 opacity-100'
                      : activeAnnotation === a.id
                        ? 'z-50 opacity-100'
                        : 'z-0 opacity-0 pointer-events-none'
                  }`}
                  style={{ top: a.top, left: a.left }}
                  onMouseEnter={() => setActiveAnnotation(a.id)}
                  onMouseLeave={() => setActiveAnnotation(null)}
                >
                  <span className="absolute inset-0 m-auto w-5 h-5 rounded-full bg-videomule-green/30 animate-ping" />
                  <button
                    className={`relative w-5 h-5 rounded-full border-2 border-white shadow-lg transition-transform duration-200 ${
                      activeAnnotation === a.id
                        ? 'bg-videomule-green scale-150'
                        : 'bg-videomule-green/80 scale-100'
                    }`}
                    aria-label={a.label}
                  />

                  <AnimatePresence>
                    {activeAnnotation === a.id && (
                      <motion.div
                        initial={{
                          opacity: 0,
                          y: topNum > 80 ? -6 : 6,
                          scale: 0.95
                        }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{
                          opacity: 0,
                          y: topNum > 80 ? -6 : 6,
                          scale: 0.95
                        }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="absolute w-52 rounded-xl bg-popover border border-border shadow-xl p-3 pointer-events-none z-[100]"
                        style={{
                          left: tooltipLeft,
                          right: tooltipRight,
                          transform: `translateX(${tooltipTranslateX})`,
                          top: tooltipTop,
                          bottom: tooltipBottom
                        }}
                      >
                        <p className="text-sm font-semibold text-foreground">
                          {a.label}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                          {a.desc}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Callouts */}
        <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            {
              label: 'Multi-track Timeline',
              desc: 'Video, audio & caption layers'
            },
            {
              label: 'Auto Zoom-ins & Callouts',
              desc: 'Highlight key actions and guide viewer attention'
            },
            {
              label: 'Voice Sync',
              desc: 'AI narration aligned to your screen'
            },
            {
              label: 'One-click Export',
              desc: 'HD video ready to publish'
            }
          ].map((item) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="rounded-xl bg-card-green border border-border/50 p-4 sm:p-5 text-center"
            >
              <p className="text-sm sm:text-base font-semibold text-foreground">
                {item.label}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
